/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
// the store is reached by path rather than by the `@/` alias, as the hooks
// beside this one do. The commit gate runs coverage with jest driven from
// the repo root, where that alias resolves to a `src` the package does not
// have - a file which leans on it cannot be covered ...
import {setTimeMode} from '../../../../../store/actions/tracking';
import {timeModeSelector} from '../../../../../store/selectors/tracking-selectors';

/**
 * Tracks the time mode the map is in - Live, Timeline or History - and the
 * handler which moves it, as the map's Time Mode control reads and reports
 * them.
 *
 * The panel each mode runs with is not gathered here: it is mounted onto the
 * map by `TimeModePanelContainer`, which owns what that panel is opened with
 * and what becomes of the choices made in it.
 *
 * ```typescript
 *
 *   import useTimeModeProps from './useTimeModeProps';
 *   ...
 *
 *   const {timeMode, onTimeModeChange} = useTimeModeProps();
 *
 * ```
 *
 * @returns The active time mode and the handler which changes it.
 */
export const useTimeModeProps = () => {
  const dispatch = useDispatch();
  const timeMode = useSelector(timeModeSelector);

  const onTimeModeChange = useCallback(mode => {
    dispatch(setTimeMode(mode));
  }, []);

  return {
    timeMode,
    onTimeModeChange
  };
};

export default useTimeModeProps;
